import { useEffect, useState } from "react";
import { Plus, Trash2, Save, X, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import SortableList from "@/components/admin/SortableList";
import MediaDropzone from "@/components/admin/MediaDropzone";

const EMPTY = { quote: "", author: "", role: "", rating: 5, image_url: "", public_id: "", kind: "couple", order: 0 };
const KINDS = ["couple", "parent", "google", "whatsapp", "screenshot", "video"];

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    return api.get("/testimonials").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async (it) => {
    try {
      const payload = { ...it, rating: Number(it.rating) || 5, order: it.order ?? 0 };
      if (it.id) {
        const { data } = await api.patch(`/testimonials/${it.id}`, payload);
        setItems((all) => all.map((x) => (x.id === data.id ? data : x)).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
        toast.success("Updated");
      } else {
        const { data } = await api.post("/testimonials", { ...payload, order: items.length + 1 });
        setItems((all) => [...all, data]);
        toast.success("Added");
      }
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await api.delete(`/testimonials/${id}`);
      setItems((all) => all.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const reorder = async (ids, next) => {
    setItems(next);
    try { await api.post("/testimonials/reorder", { ids }); }
    catch { toast.error("Reorder failed"); load(); }
  };

  return (
    <div data-testid="admin-testimonials-page">
      <header className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="uppercase tracking-[0.3em] text-[10px] text-charcoal/50 mb-2">Reviews</div>
          <h1 className="font-display text-3xl md:text-4xl text-charcoal">Testimonials</h1>
          <p className="text-charcoal/65 font-light mt-2">Drag to reorder. Add WhatsApp screenshots, Google reviews, video stills.</p>
        </div>
        <button type="button" onClick={() => setEditing({ ...EMPTY })} data-testid="testimonials-add-btn" className="btn-primary h-11 px-5 text-[11px]">
          <Plus className="h-4 w-4" /> Add testimonial
        </button>
      </header>

      {loading ? (
        <div className="text-charcoal/40">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-charcoal/40 italic">No testimonials yet.</div>
      ) : (
        <SortableList
          items={items}
          onReorder={reorder}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
          renderItem={(t, Handle) => (
            <div data-testid={`testimonial-card-${t.id}`} className="relative bg-white border border-beige p-6 pr-12 group">
              <div className="absolute top-3 left-3 z-10">{Handle}</div>
              <div className="ml-10">
                <div className="flex items-center gap-1 text-gold mb-3">
                  {Array.from({ length: t.rating || 5 }).map((_, k) => <Star key={k} className="h-3.5 w-3.5 fill-current" strokeWidth={0} />)}
                  <span className="ml-2 uppercase tracking-[0.22em] text-[10px] text-charcoal/45">{t.kind}</span>
                </div>
                <blockquote className="font-display text-lg text-charcoal/90 leading-snug">&ldquo;{t.quote}&rdquo;</blockquote>
                <div className="mt-4 pt-3 border-t border-beige flex items-end justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {t.image_url ? <img src={t.image_url} alt={t.author} className="h-10 w-10 object-cover border border-beige" /> : null}
                    <div>
                      <div className="font-display text-base text-charcoal">{t.author}</div>
                      <div className="text-xs text-charcoal/55">{t.role || "—"}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute top-3 right-3 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button type="button" onClick={() => setEditing(t)} className="h-8 w-8 border border-beige bg-ivory inline-flex items-center justify-center text-charcoal hover:bg-teal-600 hover:text-ivory">
                  <Save className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={() => remove(t.id)} className="h-8 w-8 border border-beige bg-ivory inline-flex items-center justify-center text-rose-700 hover:bg-rose-700 hover:text-ivory">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        />
      )}

      {editing ? <EditModal item={editing} onClose={() => setEditing(null)} onSave={save} /> : null}
    </div>
  );
}

function EditModal({ item, onClose, onSave }) {
  const [form, setForm] = useState({ ...item });
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.name === "rating" ? Number(e.target.value) : e.target.value }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="testimonial-edit-modal">
      <div className="absolute inset-0 bg-charcoal/45" onClick={onClose} />
      <div className="relative bg-ivory border border-beige w-full max-w-xl p-7 max-h-[90vh] overflow-y-auto">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 h-9 w-9 border border-beige hover:bg-charcoal hover:text-ivory inline-flex items-center justify-center">
          <X className="h-4 w-4" />
        </button>
        <div className="font-display text-2xl text-charcoal mb-5">{form.id ? "Edit testimonial" : "Add testimonial"}</div>

        <div className="mb-5">
          <MediaDropzone
            folder="mostash/testimonials"
            accept="image"
            height="aspect-square"
            label="Avatar / screenshot (optional)"
            testid="testimonial-image-dropzone"
            value={form.image_url ? { url: form.image_url, public_id: form.public_id, resource_type: "image" } : null}
            onChange={(asset) => setForm((f) => ({ ...f, image_url: asset?.url || "", public_id: asset?.public_id || "" }))}
          />
        </div>

        <div className="space-y-4">
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Quote *</span>
            <textarea data-testid="testimonial-edit-quote" name="quote" value={form.quote} onChange={onChange} rows={4} className="mt-2 field-textarea" />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Author *</span>
              <input data-testid="testimonial-edit-author" name="author" value={form.author} onChange={onChange} className="mt-2 field-input" />
            </label>
            <label className="block">
              <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Role / Context</span>
              <input name="role" value={form.role || ""} onChange={onChange} className="mt-2 field-input" placeholder="Mother of the bride · Mumbai" />
            </label>
            <label className="block">
              <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Type</span>
              <select name="kind" value={form.kind} onChange={onChange} className="mt-2 field-input bg-white">
                {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Rating (1–5)</span>
              <input type="number" min="1" max="5" name="rating" value={form.rating ?? 5} onChange={onChange} className="mt-2 field-input" />
            </label>
          </div>
        </div>

        <div className="mt-7 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary h-10 px-5 text-[11px]">Cancel</button>
          <button type="button" onClick={() => onSave(form)} data-testid="testimonial-edit-save" className="btn-primary h-10 px-5 text-[11px]">Save</button>
        </div>
      </div>
    </div>
  );
}
